import React, { createContext, useContext, useEffect, useState } from 'react'
import { contractTypes } from '../../constants/contractTypes'
import type { JobAdsFormType } from '../../types/career.types'

type StorageProviderProps = {
  storageKey: string
  children: React.ReactNode
}

type StorageContext = {
  storageData: JobAdsFormType
  setStorageData: (jobOfferFormValues: JobAdsFormType) => void
  resetStorageData: () => void
  isReady: boolean
}

export const defaultValues: JobAdsFormType = {
  departmentCodeQuery: undefined,
  searchQuery: undefined,
  sortBy: 'published_at',
  sortOrder: 'desc',
  contractType: undefined,
  contractDuration: undefined,
  yearsOfExperience: undefined,
  institutionType: undefined,
  context: undefined,
}

export const sanitizeStoredFilters = (data: JobAdsFormType): JobAdsFormType => {
  const contractType = data.contractType?.filter((value) =>
    contractTypes.includes(value),
  )
  return {
    ...data,
    contractType: contractType?.length ? contractType : undefined,
  }
}

const LocalStorageContext = createContext<StorageContext>({
  storageData: { ...defaultValues },
  setStorageData: () => undefined,
  resetStorageData: () => undefined,
  isReady: false,
})

export const JobAdsFiltersStorageProvider = ({
  storageKey,
  children,
}: StorageProviderProps) => {
  const [storageData, setStorageDataState] =
    useState<JobAdsFormType>(defaultValues)
  const [isReady, setIsReady] = useState<boolean>(false)

  const setStorageData = (data: JobAdsFormType) => {
    setStorageDataState(data)
    try {
      localStorage.setItem(storageKey, JSON.stringify(data))
    } catch {
      return
    }
  }

  const resetStorageData = () => {
    setStorageDataState(defaultValues)
    try {
      localStorage.removeItem(storageKey)
    } catch {
      return
    }
  }

  const refreshStorageData = () => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw) as JobAdsFormType
        const sanitized = sanitizeStoredFilters(parsed)
        if (sanitized.contractType?.length !== parsed.contractType?.length) {
          localStorage.setItem(storageKey, JSON.stringify(sanitized))
        }
        setStorageDataState(sanitized)
      }
    } catch {
      return
    }
  }

  useEffect(() => {
    refreshStorageData()
    setIsReady(true)
  }, [])

  return (
    <LocalStorageContext.Provider
      value={{
        storageData,
        setStorageData,
        resetStorageData,
        isReady,
      }}
    >
      {children}
    </LocalStorageContext.Provider>
  )
}

export const useJobAdsFiltersStorage = () => useContext(LocalStorageContext)
